import { sort, chain } from 'radash'
import snakeToCamel from '@/utils/snakeToCamel'
import semver from '@/utils/semver'

import type {
  Module,
  ModuleBadgeV2,
  ModuleBasic,
  ModuleFromApi,
  ModuleFromApiV2,
  ModuleFull,
  Pagination,
  ModulePrice,
  ModuleReleaseV2,
} from '@/types/modules.interfaces'

export const moduleV1Mapper = (module: ModuleFromApi): Module => {
  const toFloat = [
    'categoryId',
    'categoryParentId',
    'nbRates',
    'avgRate',
    'position',
  ]

  return Object.entries(module).reduce((acc, [key, value]) => {
    const newKey: string = snakeToCamel(key)
    const newValue = toFloat.includes(key) ? parseFloat(value as string) : value

    return {
      ...acc,
      [newKey as keyof Module]: newValue,
      loading: false,
    }
  }, {} as Module)
}

export const moduleV2fromApiV1Mapper = (
  module: ModuleFromApi,
  currency: keyof ModulePrice,
) => ({
  author: module.author,
  avgRate: module.avgRate,
  badges: module.badges?.map(({ img, label }) => ({
    label,
    name: img?.split('/')?.at(-1)?.split('.')?.at(0),
  })),
  categoryId: module.categoryId,
  categoryName: module.categoryName,
  categoryParentId: module.categoryParentId,
  categoryParentEnglishName: module.categoryParentEnglishName,
  categoryParentName: module.categoryParentName,
  description: module.description,
  displayName: module.displayName,
  id: module.id,
  img: module.img,
  name: module.name,
  nbRates: parseInt(module.nbRates || '0'),
  prices: {
    currency: currency,
    totalPrice: module.price?.[currency],
  },
  url: module.url,
  version: module.version,
})

export const moduleV2MapperFull = (
  moduleFromApi: ModuleFromApiV2,
  shopVersion: string,
  category?: any,
): ModuleFull => {
  const releases = moduleFromApi?.technical_info?.releases?.filter(
    (release) =>
      semver.lowerOrEqual(release.compatible_from, shopVersion) &&
      semver.greaterOrEqual(release.compatible_to, shopVersion),
  )

  const badges: ModuleBadgeV2[] = moduleFromApi?.marketing_info?.badges
    ?.filter((badge) => badge.key !== 'compatible')
    .map((badge) => ({
      label: badge.name,
      name: badge.key,
    }))

  return {
    description: moduleFromApi?.product_lang?.short_description,
    additionalInformation: moduleFromApi?.product_lang?.ebenefits,
    benefits: moduleFromApi?.product_lang?.cbenefits,
    features: moduleFromApi?.product_lang?.feature,
    author: moduleFromApi?.owner?.manufacturer?.name,
    avgRate: Number(Math.round(moduleFromApi?.ratings?.rate * 2) / 2),
    badges,
    categoryId:
      category?.categoryId || moduleFromApi.breadcrumb?.at(-2)?.id_category,
    categoryName:
      category?.categoryName || moduleFromApi?.product_lang?.category_name,
    categoryEnglishName: moduleFromApi?.breadcrumb?.[2]?.english_name,
    categoryParentId:
      category?.categoryParentId ||
      moduleFromApi.breadcrumb?.at(-2)?.id_category_parent,
    categoryParentEnglishName:
      category?.categoryParentEnglishName ||
      moduleFromApi?.breadcrumb?.[1]?.english_name,
    categoryParentName:
      category?.categoryParentName || moduleFromApi?.breadcrumb?.[1]?.name,
    technicalInfo: { ...moduleFromApi.technical_info, releases },
    gallery: {
      ...moduleFromApi.gallery,
      video: moduleFromApi?.gallery?.video,
    },
    marketingInfo: { ...moduleFromApi.marketing_info },
    displayName: moduleFromApi?.product_lang?.product_name,
    id: moduleFromApi?.product?.id_product?.toString(),
    img: `https://addons.prestashop.com/img/pico/${moduleFromApi?.product?.id_product}.jpg`,
    name: moduleFromApi?.technical_info?.technical_name,
    nbRates: moduleFromApi?.ratings?.nb_rates,
    prices: {
      currency: moduleFromApi?.prices?.currency,
      productPrice: moduleFromApi.prices?.product?.actual_price,
      productOriginalPrice: moduleFromApi.prices?.product?.original_price,
      businessCarePrice: moduleFromApi.prices?.business_care?.actual_price,
      businessCareOriginalPrice:
        moduleFromApi.prices?.business_care?.original_price,
      totalPrice: moduleFromApi.prices?.total?.actual_price,
      totalOriginalPrice: moduleFromApi.prices?.total?.original_price,
      reduction: moduleFromApi.prices?.product?.promotion?.reduction,
      reductionType: moduleFromApi.prices?.product?.promotion?.reduction_type,
    },
    originalEuroTotalPrice: moduleFromApi.prices?.total?.euro_price,
    actualEuroTotalPrice: moduleFromApi.prices?.total?.actual_euro_price,
    purchased: moduleFromApi.purchased,
    url: moduleFromApi?.product_lang?.product_link,
    version: getLastAvailableVersionFromRelease(
      moduleFromApi?.technical_info?.releases,
      shopVersion,
    ),
  }
}

function getLastAvailableVersionFromRelease(
  releases: ModuleReleaseV2[],
  shopVersion: string,
) {
  const order = (): ModuleReleaseV2[] =>
    sort(
      releases,
      (release) => semver.semVerToInt(release.product_version),
      true,
    )
  const findLastRelease = (releases: ModuleReleaseV2[]) =>
    releases.find(
      (release) =>
        semver.lowerOrEqual(release.compatible_from, shopVersion) &&
        semver.greaterOrEqual(release.compatible_to, shopVersion),
    )
  const getVersion = (release?: ModuleReleaseV2): string | undefined =>
    release?.product_version
  const chained: () => string = chain(order, findLastRelease, getVersion)
  return chained()
}

export const moduleV2Mapper = (product: any) => ({
  author: product.manufacturer_name,
  avgRate: Number(product.note_avg),
  badges: product.badges?.product?.filter(
    (badge: any) => badge.name !== 'compatible',
  ),
  categoryId: product.id_category_default,
  categoryName: product.category_name,
  categoryEnglishName: product.category_english_name,
  categoryParentId: product.parent_category_id?.toString(),
  categoryParentEnglishName: product.parent_category_english_name,
  categoryParentName: product.parent_category_name,
  description: product.description,
  displayName: product.name,
  id: product.id_product,
  img: product.pico,
  name: product.technical_name,
  nbRates: parseInt(product.nb_rates),
  prices: {
    currency: product?.prices?.currency,
    productPrice: product?.prices?.product?.actual_price,
    productOriginalPrice: product?.prices?.product?.original_price,
    businessCarePrice: product?.prices?.business_care?.actual_price,
    businessCareOriginalPrice: product?.prices?.business_care?.original_price,
    totalPrice: product?.prices?.total?.actual_price,
    totalOriginalPrice: product?.prices?.total?.original_price,
    reduction: product?.prices?.product?.promotion?.reduction,
    reductionType: product?.prices?.product?.promotion?.reduction_type,
  },
  originalEuroTotalPrice: product?.prices?.total?.euro_price,
  actualEuroTotalPrice: product?.prices?.total?.actual_euro_price,
  url: product.product_link,
  version: product.available_version,
})

export const searchMapper = (response: any) => {
  const results = response.results

  const pagination: Pagination = {
    currentPage: results.stats.current_page,
    pageSize: results.stats.results_per_page,
    total: results.stats.total_results,
    nbPages: results.stats.total_pages,
  }

  const categories =
    results.facets?.category_search?.facets?.map((facet: any) => ({
      id: parseInt(facet.facet_value),
      nbResult: facet.nb_results,
    })) || []

  const sortKey = results.stats.sorting_code

  const sortOpts = Object.values(results.sortings as object).map(
    ({ sorting_code, label }) => ({
      value: sorting_code,
      label,
    }),
  )

  const moduleSortedByNames = results.ids.map(
    (id: string) => results.products[id].technical_name,
  )

  const modules: ModuleBasic[] = Object.values(results.products).map(
    moduleV2Mapper,
  )

  const developers =
    results.facets?.developed_by?.facets?.map((facet: any) => {
      return {
        label: facet.facet_name,
        value: facet.facet_value,
        nbResult: facet.nb_results,
      }
    }) || []

  const ratingOpts = results.facets?.['pred-moyenne-avis']?.facets?.map(
    ({ facet_name, facet_value, nb_results }: Record<string, any>) => ({
      label: facet_name,
      value: facet_value,
      nbResults: nb_results,
    }),
  )

  return {
    categories,
    pagination,
    modules,
    moduleSortedByNames,
    sortOpts,
    sortBy: sortKey,
    developers,
    ratingOpts,
  }
}
